import React, { useState, useEffect } from 'react';
import { getThemeColors, spacing, typography, borderRadius } from '../utils/theme';
import { API_ENDPOINTS } from '../config/api';

interface UserDashboardHeaderProps {
  title?: string;
  subtitle?: string;
  showBackButton?: boolean;
  showHomeButton?: boolean;
  showProfilePic?: boolean;
  showNotificationBell?: boolean;
  rightComponent?: React.ReactNode;
  leftComponent?: React.ReactNode;
  style?: React.CSSProperties;
  titleStyle?: React.CSSProperties;
  backgroundColor?: string;
  onBackPress?: () => void;
  onHomePress?: () => void;
  onProfilePress?: () => void;
}

/**
 * Enhanced User Dashboard Header Component
 * Universal header with profile pic, home icon, smart navigation, and centered title
 * Layout: [Home Icon] [Back Button] [Title (Center)] [Notification Bell] [Profile Pic]
 * Acts as a fixed header allowing content to scroll freely underneath
 */
const UserDashboardHeader: React.FC<UserDashboardHeaderProps> = ({
  title,
  subtitle,
  showBackButton = true,
  showHomeButton = true,
  showProfilePic = true,
  showNotificationBell = true,
  rightComponent,
  leftComponent,
  style,
  titleStyle,
  backgroundColor,
  onBackPress,
  onHomePress,
  onProfilePress,
}) => {
  const colors = getThemeColors('dark');
  const [avatarData, setAvatarData] = useState<string | null>(null);
  const [userData, setUserData] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });

  const headerBackgroundColor = backgroundColor || colors.surface;

  // Handle home navigation
  const handleHomePress = () => {
    if (onHomePress) {
      onHomePress();
    } else {
      // Navigate to user dashboard home
      window.location.href = '/dashboard';
    }
  };

  // Handle profile navigation
  const handleProfilePress = () => {
    if (onProfilePress) {
      onProfilePress();
    } else {
      // Navigate to user profile/settings
      window.location.href = '/dashboard/profile';
    }
  };

  // Handle back navigation
  const handleBackPress = () => {
    if (onBackPress) {
      onBackPress();
    } else {
      // Default back behavior
      window.history.back();
    }
  };

  // Load user profile data and avatar from API
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const authToken = localStorage.getItem('authToken');
        if (!authToken) {
          console.error('No auth token found');
          setAvatarData(null);
          return;
        }

        const response = await fetch(API_ENDPOINTS.USER_PROFILE, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();
        if (data.success) {
          const apiUser = data.data.user || data.data;

          // Store user data for initials fallback
          setUserData({
            firstName: apiUser.firstName || apiUser.first_name || '',
            lastName: apiUser.lastName || apiUser.last_name || '',
            email: apiUser.email || '',
          });

          // Handle avatar data from API
          if (apiUser.avatar) {
            let avatarUrl = apiUser.avatar;
            if (!avatarUrl.startsWith('data:image/')) {
              avatarUrl = `data:image/jpeg;base64,${avatarUrl}`;
            }
            setAvatarData(avatarUrl);
          } else {
            setAvatarData(null);
          }
        } else {
          console.error('Failed to load user profile:', data.error);
          setAvatarData(null);
          setUserData({ firstName: '', lastName: '', email: '' });
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
        setAvatarData(null);
      }
    };

    loadUserProfile();
  }, []);

  // Get user initials for profile pic fallback
  const getUserInitials = () => {
    try {
      const storedUserData = localStorage.getItem('userData');
      if (storedUserData) {
        const user = JSON.parse(storedUserData);
        if (user?.firstName && user?.lastName) {
          return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
        }
        if (user?.first_name && user?.last_name) {
          return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
        }
        if (user?.email) {
          return user.email.charAt(0).toUpperCase();
        }
      }
    } catch (error) {
      console.error('Error getting user initials:', error);
    }
    return 'U';
  };

  const styles: { [key: string]: React.CSSProperties } = {
    safeArea: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingLeft: spacing.md,
      paddingRight: spacing.md,
      paddingTop: spacing.sm,
      paddingBottom: spacing.sm,
      minHeight: 60,
      backgroundColor: headerBackgroundColor,
      borderBottom: `1px solid ${colors.border}`,
      ...style,
    },
    leftSection: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
    },
    leftButtons: {
      display: 'flex',
      alignItems: 'center',
    },
    centerSection: {
      flex: 2,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      paddingLeft: spacing.sm,
      paddingRight: spacing.sm,
    },
    rightSection: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
    },
    rightComponents: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing.sm,
    },
    iconButton: {
      padding: spacing.xs,
      borderRadius: 20,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    homeButton: {
      marginRight: spacing.xs,
    },
    backButtonSpacing: {
      marginLeft: spacing.xs,
    },
    title: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.semibold,
      textAlign: 'center',
      maxWidth: '100%',
      color: colors.text,
      ...titleStyle,
    },
    subtitle: {
      fontSize: typography.fontSize.sm,
      marginTop: spacing.xs,
      textAlign: 'center',
      maxWidth: '100%',
      color: colors.textSecondary,
    },
    profileContainer: {
      padding: spacing.xs,
      cursor: 'pointer',
    },
    profilePic: {
      width: 40,
      height: 40,
      borderRadius: 20,
      border: `2px solid ${colors.primary}`,
      objectFit: 'cover',
    },
    profilePicFallback: {
      width: 40,
      height: 40,
      borderRadius: 20,
      border: `2px solid ${colors.primary}`,
      backgroundColor: colors.primary + '20',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    profileInitials: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.bold,
      color: colors.primary,
    },
    notificationBell: {
      position: 'relative',
      padding: spacing.sm,
      cursor: 'pointer',
    },
    notificationIcon: {
      fontSize: 24,
      color: colors.text,
    },
    badge: {
      position: 'absolute',
      top: 6,
      right: 6,
      minWidth: 18,
      height: 18,
      borderRadius: 9,
      backgroundColor: colors.error,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      paddingLeft: 4,
      paddingRight: 4,
    },
    badgeText: {
      fontSize: 10,
      fontWeight: 'bold',
      color: colors.white,
      textAlign: 'center',
    },
  };

  return (
    <div style={styles.safeArea}>
      <div style={styles.header}>
        {/* Left Section: Home Icon + Back Button */}
        <div style={styles.leftSection}>
          {leftComponent || (
            <div style={styles.leftButtons}>
              {/* Smart Back Button */}
              {showBackButton && (
                <button
                  style={{
                    ...styles.iconButton,
                    ...styles.backButtonSpacing,
                    backgroundColor: 'transparent',
                    border: 'none',
                  }}
                  onClick={handleBackPress}
                >
                  <span style={{ fontSize: 24, color: colors.text }}>←</span>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Center Section: Title */}
        <div style={styles.centerSection}>
          {title && (
            <div style={styles.title}>
              {title}
            </div>
          )}
          {subtitle && (
            <div style={styles.subtitle}>
              {subtitle}
            </div>
          )}
        </div>

        {/* Right Section: Notification Bell & Profile Pic */}
        <div style={styles.rightSection}>
          {rightComponent || (
            <div style={styles.rightComponents}>
              {/* Notification Bell */}
              {showNotificationBell && (
                <div
                  style={styles.notificationBell}
                  onClick={() => window.location.href = '/dashboard/notifications'}
                >
                  <span style={styles.notificationIcon}>🔔</span>
                  {/* Mock notification badge */}
                  <div style={styles.badge}>
                    <span style={styles.badgeText}>3</span>
                  </div>
                </div>
              )}

              {/* Profile Picture */}
              {showProfilePic && (
                <div
                  style={styles.profileContainer}
                  onClick={handleProfilePress}
                >
                  {avatarData ? (
                    <img
                      src={avatarData}
                      alt="Profile"
                      style={styles.profilePic}
                      onError={() => setAvatarData(null)}
                    />
                  ) : (
                    <div style={styles.profilePicFallback}>
                      <span style={styles.profileInitials}>
                        {getUserInitials()}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDashboardHeader;